import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ProductsService } from '@/services/products.service';
import type { FunnelData } from '@/pages/EditProduct/FunnelMap/utils/funnelTypes';

export const FUNNEL_QUERY_KEY = 'product-funnel';

interface UseFunnelDataReturn {
  funnelData: FunnelData | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  invalidate: () => Promise<void>;
  refetch: () => void;
}

export function useFunnelData(productId: string): UseFunnelDataReturn {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error, refetch } = useQuery<FunnelData>({
    queryKey: [FUNNEL_QUERY_KEY, productId],
    queryFn: () => ProductsService.getFunnel(productId),
    enabled: !!productId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const invalidate = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: [FUNNEL_QUERY_KEY, productId] });
  }, [queryClient, productId]);

  return {
    funnelData: data ?? null,
    isLoading,
    isFetching,
    error: error as Error | null,
    invalidate,
    refetch,
  };
}
